import { useSource } from "@opendash/core";
import { Source } from "@opendash/plugin-parse/dist/types-generated";
import { Select } from "antd";
import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo, useState } from "react";
import { useItemsForSources } from "../hooks/useItemsForSources";

type CustomFormItemPropsMultiple = {
  value?: string[];
  onChange?: (itemkeys: string[]) => void;
  mode?: "multiple";
};

type CustomFormItemPropsSingle = {
  value?: string;
  onChange?: (itemkey: string) => void;
  mode?: "single";
};

type CustomFormItemPropsDefault = {
  style?: React.CSSProperties;
  /**
   * Defaults to the selected source in the header
   */
  sources?: string[] | Source[];
};

type CustomFormItemProps = (
  | CustomFormItemPropsMultiple
  | CustomFormItemPropsSingle
) &
  CustomFormItemPropsDefault;

/**
 * Wrap it in to a <Form.Item> component. Initial value can be set by providing `initialValue={...}`.
 * @param param0 An object containing `{value, onChange}` where value uses initialValue from <Form.Item> and onChange propagates the input back to <Form.Value>. `value` should be an array of strings.
 * @returns Provides a `<Select />` component.
 */
export const ItemSelect: React.FC<CustomFormItemProps> = observer(
  ({ value, onChange, sources, mode, style }) => {
    const items = useItemsForSources(sources);
    const [selectedItems, setSelectedItems] = useState<string[] | string>(
      value ?? undefined
    );
    const [selectedSource] = useSource();
    const change = (key: Array<string> | string) => {
      setSelectedItems(key);
      if (key instanceof Array) {
        (onChange as (itemkeys: string[]) => void)?.(key);
      } else {
        (onChange as (itemkey: string) => void)?.(key);
      }
    };

    useEffect(() => {
      // check if the selected items are still valid
      if (selectedItems && selectedItems?.length > 0) {
        const validItems = items.filter((item) =>
          selectedItems.includes(item.id)
        );
        if (validItems.length !== selectedItems.length)
          change(validItems.map((item) => item.id));
      }
    }, [selectedSource, sources, items]);

    useEffect(() => {
      setSelectedItems(value ?? []);
    }, [value, sources]);

    const options = useMemo(
      () => items.map((item) => ({ label: item.name, value: item.id })),
      [items]
    );

    return (
      <Select
        mode={mode === "multiple" ? mode : undefined}
        allowClear
        onChange={(value) => change(value)}
        style={{ width: "100%", ...style }}
        options={options}
        value={selectedItems}
      />
    );
  }
);
